<template>
  <div
    ref="component"
    class="wetheme-dropdown"
    :class="[
      'fit-' + fit,
      open ? 'wetheme-dropdown--open' : 'wetheme-dropdown--closed',
      'wetheme-dropdown--' + direction,
    ]"
  >
    <div v-if="title" class="option-label-wrapper">
      <label :for="id" v-text="title" class="label-font"></label>
      <div id="size-guide" class="text-link-animated">
        <a
          v-if="sizeGuideLink"
          :href="sizeGuideLink"
          id="wetheme-dropdown__size-guide"
          v-text="sizeGuideText">
        </a>
      </div>
    </div>
    <div class="wetheme-dropdown__wrapper">
      <button
        :id="id"
        ref="button"
        type="button"
        autocomplete="off"
        readonly="true"
        tabindex="0"
        :aria-controls="dropdownId"
        :aria-expanded="open"
        @click="toggleOpen"
        @blur.prevent="close"
        @keydown.up.prevent="moveFocusedIndex(-1)"
        @keydown.down.prevent="moveFocusedIndex(1)"
        @keydown.enter.prevent="applyFocused(false)"
        @keydown.space.prevent="applyFocused(false)"
        @keydown.esc.prevent="close"
      >
        <span
          v-text="selectedOption.label || '\xa0'"
          class="header--supporting-text"
        ></span>
        <span class="icon-dropdown inline-icon--wrapper">
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><path d="M5.5 7.5L10.5 12.5L15.5 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
      </button>
      <transition name="wetheme-dropdown-fade">
        <ul :id="dropdownId" ref="ul" v-show="open" class="dropdown-list">
          <li
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :class="['dropdown-list--item', { selected: option.value === options[focusedIndex].value, 'sold-out': option.soldOutText }]"
            @mousedown="optionClicked(option)"
          >
            <span class="option-label">{{ option.label }}</span>
            <!-- Optional additional text -->
            <span v-if="option.soldOutText" class="status-text">{{ option.soldOutText }}</span>
          </li>
        </ul>
      </transition>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

function getElementWidth(el) {
  const clone = el.cloneNode(true);
  clone.style.cssText =
    "position: fixed; top: 0; left: 0; overflow: auto; visibility: hidden; pointer-events: none; height: unset; max-height: unset";
  document.body.appendChild(clone);
  const width = clone.getBoundingClientRect().width;
  document.body.removeChild(clone);
  return width;
}

let dropdown_last_id = 0;

export default Vue.extend({
  props: {
    fit: {
      type: String,
      default: "parent",
      validator: function (value) {
        return ["parent", "current", "longest"].indexOf(value) !== -1;
      },
    },
    direction: {
      type: String,
      default: "down",
      validator: function (value) {
        return ["down", "up"].indexOf(value) !== -1;
      },
    },
    isVariantDropdown: {
      type: Boolean,
      default: false
    },
    soldOutString: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: "",
    },
    value: {
      type: String,
      default: null,
    },
  },
  data: function () {
    return {
      id: "",
      title: "",
      open: false,
      options: [],
      selectedOption: undefined,
      // focusedIndex is index of option that user navigated to using keyboard,
      // it's equal to index of selected option when keyboard was not used
      focusedIndex: 0,
      selectComponent: undefined,
    };
  },
  computed: {
    dropdownId: function () {
      return this.id + "-dropdown";
    },
  },
  watch: {
    value: function (newVal) {
      this.options.forEach((option, index) => {
        if (option.value === newVal) {
          this.selectedOption = option;
          this.focusedIndex = index;
        }
      });
    },
  },
  beforeMount: function () {
    // Convert a <select> and <label> into options and other properties
    const el =
      this.$root.$el.tagName === "WETHEME-DROPDOWN"
        ? this.$root.$el
        : this.$root.$el.querySelector("wetheme-dropdown");
    var select = el.querySelector("select");
    if (select.id) {
      this.id = select.id;
    } else {
      dropdown_last_id += 1;
      this.id = "wetheme-dropdown-" + dropdown_last_id;
    }
    var labelEl = el.querySelector("label");
    this.title = labelEl?.textContent;
    var sizeGuideEl = el.querySelector("#size-guide-link");
    this.sizeGuideLink = sizeGuideEl?.href;
    this.sizeGuideText = sizeGuideEl?.textContent;
    var options = [];
    Array.prototype.forEach.call(
      select.querySelectorAll("option"),
      (option, index) => {
        options.push({
          value: option.value,
          label: option.textContent,
          soldOutText: option.getAttribute('data-status-text') || null,
        });
        if (option.defaultSelected) {
          this.selectedOption = option;
          this.focusedIndex = index;
        }
      }
    );
    this.options = options;
    if (!this.selectedOption) {
      this.selectedOption = options[0];
      this.focusedIndex = 0;
    }
    this.selectWidth = getElementWidth(select);
    this.selectComponent = select;
    this.selectComponent.style.display = "none";
    this.selectComponent.addEventListener("input", console.log);
    el.parentNode.removeChild(el);
  },
  mounted: function () {
    if (this.fit === "longest") {
      var w = this.selectWidth + 20; // 20px for caret
      this.$el.style.width = w + "px";
      this.$refs.ul.style.width = w + "px";
    }
    this.$el.appendChild(this.selectComponent);
    this.productForm = this.$el.closest('.product-single-wrapper') || this.$el.closest('#product-box');

    // Check if this.productForm is available and isVariantDropdown is true
    if (this.isVariantDropdown && this.productForm) {
      this.productForm.addEventListener('variant:changed', this.handleVariantChange);
    }
  },
  methods: {
    findOptionIndex: function (option) {
      var index;
      this.options.forEach(function (o, i) {
        if (o.value === option.value) {
          index = i;
        }
      });
      return index;
    },
    optionClicked: function (option) {
      this.selectedOption = option;
      this.focusedIndex = this.findOptionIndex(option);
      this.selectComponent.value = option.value;
      this.open = false;
      this.$nextTick(function () {
        // Emit the event when input value is already changed
        var e = new CustomEvent("change");
        this.selectComponent.dispatchEvent(e);
      });
      this.$emit("change", option.value, this.name);
    },
    moveFocusedIndex: function (offset) {
      var focusedIndex = this.focusedIndex + offset;
      if (focusedIndex >= 0 && focusedIndex < this.options.length) {
        this.focusedIndex = focusedIndex;
      }
      if (!this.open) {
        this.applyFocused(true);
      }
    },
    toggleOpen: function () {
      this.$nextTick(function () {
        if (!this.open) {
          this.open = true;
          this.$refs.button.focus();
        } else {
          this.open = false;
          this.$refs.button.blur();
        }
      });
    },
    applyFocused: function (force) {
      if (!this.open && !force) {
        this.open = true;
        return;
      }
      this.optionClicked(this.options[this.focusedIndex]);
      this.open = false;
    },
    handleVariantChange(event) {
      const product = event.detail.product;
      if (!product || product.options.length === 1) return;

      const variant = event.detail.variant;
      if (!variant) return;

      this.$nextTick(() => {
        product.options.forEach((option, index) => {
          const optionValues = product.variants.map(variant => variant[`option${index + 1}`]);
          optionValues.forEach(value => {
            this.UpdateDropdownState(product, value, index + 1, variant, option);
          });
        });
      });
    },
    UpdateDropdownState(product, value, optionIndex, currentVariant) {
      const availability = product.variants.map(variant => variant.available);
			const optionValues = [
				product.variants.map(variant => variant.option1),
				product.variants.map(variant => variant.option2),
				product.variants.map(variant => variant.option3),
			];
		
			const isOptionAvailable = optionValues[optionIndex - 1].some((optionValue, i) => {
				switch (optionIndex) {
					case 1:
						return optionValue === value && availability[i];
					case 2:
						return optionValue === value && optionValues[0][i] === currentVariant.option1 && availability[i];
					case 3:
						return optionValue === value && optionValues[0][i] === currentVariant.option1 &&
						optionValues[1][i] === currentVariant.option2 && availability[i];
					default:
						return false;
				}
			});
		
			const dropdowns = this.productForm.querySelectorAll(`.dropdown-list--item[value="${value}"]`);
			dropdowns.forEach(dropdown => {
				if (isOptionAvailable) {
					dropdown.classList.remove('sold-out');
          // Remove sold-out text if it exists
          const statusText = dropdown.querySelector('.status-text');
          if (statusText) {
            statusText.remove();
          }
				} else {
					dropdown.classList.add('sold-out');
          // Add sold-out text if it doesn't exist
          if (!dropdown.querySelector('.status-text')) {
            const soldOutText = document.createElement('span');
            soldOutText.className = 'status-text';
            soldOutText.textContent = this.soldOutString;
            dropdown.appendChild(soldOutText);
          }
				}
			});
    },
    close: function () {
      this.open = false;
    },
  },
  beforeDestroy: function () {
    if (this.isVariantDropdown && this.productForm) {
      this.productForm.removeEventListener('variant:changed', this.handleVariantChange);
    }
  }
});
</script>